import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ExpWithDogsPage.css";
import { ArrowLeft, Check } from "lucide-react";
import progressIcon from "../../assets/progressIcon8.png";
import nextIcon from "../../assets/nextButtonIcon2.png";
import dogLevel0 from "../../assets/dogLevel0.png";
import dogLevel1 from "../../assets/dogLevel1.png";
import dogLevel2 from "../../assets/dogLevel2.png";
import dogLevel3 from "../../assets/dogLevel3.png";
import dogLevel4 from "../../assets/dogLevel4.png";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";

const ExpWithDogsPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);

  const dogName = formData.dogName || "{Dog Name}";

  const TRACK_START = 5;
  const TRACK_END = 95;
  const TRACK_RANGE = TRACK_END - TRACK_START;
  const SLIDER_STEPS = 4;

  const experienceLevels = [
    { value: 0, label: "not friendly with any other dogs" },
    { value: 1, label: "not friendly with most dogs" },
    { value: 2, label: "50/50 friendly with dogs" },
    { value: 3, label: "friendly with most dogs" },
    { value: 4, label: "friendly with all other dogs" },
  ];

  const getInitialValue = () => {
    if (formData.socialExperienceLevelWithDogs?.value !== undefined) {
      return formData.socialExperienceLevelWithDogs.value;
    }
    const strVal =
      formData.alongWithOtherDogs || formData.along_with_other_dogs;
    if (typeof strVal === "string") {
      const foundIdx = experienceLevels.findIndex(
        (lvl) => lvl.label.toLowerCase() === strVal.toLowerCase(),
      );
      if (foundIdx !== -1) return foundIdx;
    }
    return 1;
  };

  const [sliderValue, setSliderValue] = useState(getInitialValue);

  useEffect(() => {
    setSliderValue(getInitialValue());
  }, [formData]);

  const handleSliderChange = (value) => {
    const clamped = Math.max(0, Math.min(SLIDER_STEPS, value));
    setSliderValue(clamped);
  };

  const handleNext = () => {
    const selectedObj = experienceLevels[sliderValue];
    setFormData((prev) => ({
      ...prev,
      socialExperienceLevelWithDogs: {
        value: sliderValue,
        label: selectedObj.label,
      },
      alongWithOtherDogs: selectedObj.label,
      along_with_other_dogs: selectedObj.label,
    }));

    navigate("/exp-with-humans");
  };

  const handleSkip = () => {
    navigate("/exp-with-humans");
  };

  const dogLevelImages = [
    dogLevel0,
    dogLevel1,
    dogLevel2,
    dogLevel3,
    dogLevel4,
  ];

  const currentDogLevelImage = dogLevelImages[sliderValue] || dogLevel0;

  const updateValueFromPosition = (clientX, clientY) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 1023;

    let percent;

    if (isMobile) {
      let y = clientY - rect.top;
      y = Math.max(0, Math.min(rect.height, y));
      percent = y / rect.height;
    } else {
      let x = clientX - rect.left;
      x = Math.max(0, Math.min(rect.width, x));
      percent = x / rect.width;
    }

    const steppedValue = Math.round(percent * SLIDER_STEPS);
    handleSliderChange(steppedValue);
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    updateValueFromPosition(clientX, clientY);
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", stopDragging);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", stopDragging);
    };
  }, []);

  const valPosPercent = `${TRACK_START + (sliderValue / SLIDER_STEPS) * TRACK_RANGE}%`;

  return (
    <div className="paws-exp-container-d">
      <MobileTopBar
        onBack={() => {
          navigate("/dimsum-availability", {
            state: { returnToStep: 1 },
            replace: true,
          });
        }}
        backColor="#000"
        logoColor="#009fd9"
        blueLogo="true"
        isProtectedRoute={false}
      />

      <DesktopTopBar
        showBack={true}
        onBack={() => navigate("/dimsum-availability")}
        backColor="#000"
        showProgress={true}
        progressIconSrc={progressIcon}
        progressPercent={80}
        iconLeft={72}
        iconWidth="80px"
      />

      <div className="paws-exp-content-d">
        <div className="paws-mobile-progress-wrapper paws-mobile-only">
          <div className="dog-icon-top">
            <img
              src={progressIcon}
              alt="Progress"
              className="paws-progress-icon-img"
            />
          </div>
          <div
            className="main-progress-bar"
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "white",
            }}
          >
            <div className="main-progress-fill" style={{ width: "85%" }}></div>
          </div>
        </div>

        <h1 className="paws-exp-title-d">
          HOW DOES {dogName.toUpperCase()} GET ALONG WITH OTHER DOGS?
        </h1>
        <p className="paws-exp-subtitle-d">Honesty is the best Pawlicy!</p>

        <div
          className="custom-slider-container-ex"
          ref={sliderRef}
          onClick={handleTrackClick}
          style={{ "--pos": valPosPercent }}
        >
          <div className="slider-track-bg-ex"></div>
          <div className="slider-track-fill-ex"></div>

          <div className="slider-steps-wrapper-ex">
            {[...Array(SLIDER_STEPS + 1)].map((_, index) => {
              const stepPosPercent = `${TRACK_START + (index / SLIDER_STEPS) * TRACK_RANGE}%`;
              return (
                <div
                  key={index}
                  className={`slider-step-item-ex ${index % 2 === 0 ? "left-label" : "right-label"}`}
                  style={{ "--step-pos": stepPosPercent }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSliderChange(index);
                  }}
                >
                  <div className={`slider-dot-ex`}></div>
                  <span className={`slider-label-ex`}>
                    {experienceLevels[index]?.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div
            className="slider-thumb-dog-ex"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            style={{
              left:
                window.innerWidth <= 1023
                  ? sliderValue <= 2
                    ? "50%"
                    : "49%"
                  : undefined,
            }}
          >
            <img
              src={currentDogLevelImage}
              alt={`DogIcon`}
              draggable={false}
              className={`dog-level-icon ${
                sliderValue === 3 || sliderValue === 4 ? "dog-level-large" : ""
              }`}
            />
          </div>
        </div>
      </div>

      <DesktopBottomBar
        onNext={handleNext}
        isNextEnabled={true}
        nextIconSrc={nextIcon}
        showSkip={true}
        onSkip={handleSkip}
        isSkipBlue={true}
        variant="blue"
      />

      <div className="paws-exp-footer mobile-only">
        <MobileBottomBar
          onNext={handleNext}
          isNextEnabled={true}
          nextIconSrc={nextIcon}
          showSkip={true}
          onSkip={handleSkip}
          isSkipBlue={true}
          variant="blue"
        />
      </div>
    </div>
  );
};

export default ExpWithDogsPage;
